import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, PlusCircle, Briefcase, MapPin, Building2, Mail, Phone, User, Shield, CalendarDays, Users, ChevronRight, Send, X, AlertTriangle, FileText, Check, Newspaper, ListChecks, Flag, UserCheck, Cake, Palmtree, Thermometer, Trash2, Pencil, ScanLine } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';
import AnniversaryCelebration from '../../components/employee-portal/AnniversaryCelebration';
import ClockStatusPill from '../../components/employee-portal/ClockStatusPill';
import { MultiDatePicker } from '../../components/leaves/MultiDatePicker';
import { LeavePatterns, datesForNextMonth } from '../../components/employee-portal/LeavePatterns';

const API = process.env.REACT_APP_BACKEND_URL;

const LEAVE_TYPES = [
  { value: 'annual', label: 'Annual Leave' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'emergency', label: 'Emergency Leave' },
  { value: 'public_holiday', label: 'Public Holiday' },
  { value: 'unpaid', label: 'Unpaid Leave' },
  { value: 'maternity', label: 'Maternity Leave' },
];

// Show a separate-dates request as one row (its dates kept in group_rows).
const collapseGroups = (rows) => {
  const seen = {};
  return rows.reduce((out, l) => {
    if (!l.group_id) { out.push(l); return out; }
    if (seen[l.group_id]) { seen[l.group_id].group_rows.push(l); return out; }
    seen[l.group_id] = { ...l, group_rows: [l] };
    out.push(seen[l.group_id]);
    return out;
  }, []).map((l) => {
    if (!l.group_rows) return l;
    const rows2 = [...l.group_rows].sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
    const statuses = [...new Set(rows2.map((r) => r.status))];
    return { ...l, group_rows: rows2, days: rows2.length, status: statuses.length === 1 ? statuses[0] : 'partly approved' };
  });
};
const shortDate = (d) => new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

const PortalDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [balance, setBalance] = useState(null);
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [docExpiry, setDocExpiry] = useState([]);
  const [allStaffExpiry, setAllStaffExpiry] = useState([]);
  const [newsJoiners, setNewsJoiners] = useState([]);
  const [newsCustom, setNewsCustom] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [teamStatus, setTeamStatus] = useState({ on_leave: [], on_sick: [], birthdays: [], onboarded: [] });
  const [upcomingLeaves, setUpcomingLeaves] = useState({ visible: false, see_all: false, scope: [], leaves: [] });
  const [hrOnLeave, setHrOnLeave] = useState({ current: [], upcoming: [], cover_contacts: [], managers_current: [], managers_upcoming: [] });
  const [editingLeave, setEditingLeave] = useState(null);
  const [deletingLeaveId, setDeletingLeaveId] = useState(null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const emp = JSON.parse(localStorage.getItem('portal_employee') || '{}');
  const token = localStorage.getItem('portal_token');

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [profileRes, balRes, leavesRes] = await Promise.all([
        axios.get(`${API}/api/employee-portal/me`, { headers }),
        axios.get(`${API}/api/employee-portal/my-balance`, { headers }),
        axios.get(`${API}/api/employee-portal/my-leaves`, { headers })
      ]);
      setProfile(profileRes.data);
      setBalance(balRes.data);
      setRecentLeaves(collapseGroups(leavesRes.data).slice(0, 4));
      // Load document expiry status
      try {
        const expiryRes = await axios.get(`${API}/api/employee-portal/document-expiry`, { headers });
        setDocExpiry(expiryRes.data.documents || []);
      } catch (err) { console.error(err); }
      // Super admin (EMP0001): load all-staff document expiry alerts
      if ((emp?.employee_code || '').toUpperCase() === 'EMP0001') {
        try {
          const allRes = await axios.get(`${API}/api/employee-portal/all-staff-document-expiry`, { headers });
          setAllStaffExpiry(allRes.data.documents || []);
        } catch (err) { console.error(err); }
      }
      // Load HR News Desk joiners
      try {
        const newsRes = await axios.get(`${API}/api/employee-portal/news-desk`, { headers });
        setNewsJoiners(newsRes.data.joiners || []);
        setNewsCustom(newsRes.data.custom_announcements || []);
      } catch (err) { console.error(err); }
      // Load assigned tasks
      try {
        const todosRes = await axios.get(`${API}/api/employee-portal/my-todos`, { headers });
        const todos = todosRes.data.todos || [];
        setAssignedTasks(todos);
      } catch (err) { console.error(err); }
      // Load team status (leaves, sick, birthdays)
      try {
        const statusRes = await axios.get(`${API}/api/employee-portal/team-status`, { headers });
        setTeamStatus(statusRes.data);
      } catch (err) { console.error(err); }
      // Load upcoming leaves (managers see their departments, HR sees all)
      try {
        const upRes = await axios.get(`${API}/api/employee-portal/upcoming-leaves`, { headers });
        setUpcomingLeaves(upRes.data);
      } catch (err) { console.error(err); }
      // HR-on-leave notice — visible to everyone
      try {
        const hrRes = await axios.get(`${API}/api/employee-portal/hr-on-leave`, { headers });
        setHrOnLeave(hrRes.data || { current: [], upcoming: [] });
      } catch (err) { console.error(err); }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_employee');
        navigate('/employee-portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [token, navigate]);

  const handleDeleteLeave = async (leaveId) => {
    try {
      await axios.delete(`${API}/api/employee-portal/my-leaves/${leaveId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeletingLeaveId(null);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };


  const calcTenure = (joinDate) => {
    if (!joinDate) return '\u2014';
    const jd = new Date(joinDate);
    const now = new Date();
    const years = now.getFullYear() - jd.getFullYear();
    const months = now.getMonth() - jd.getMonth();
    const totalMonths = years * 12 + months;
    const y = Math.floor(totalMonths / 12);
    const m = totalMonths % 12;
    if (y > 0 && m > 0) return `${y} yr${y > 1 ? 's' : ''}, ${m} mo`;
    if (y > 0) return `${y} yr${y > 1 ? 's' : ''}`;
    return `${m} mo`;
  };

  const statusColors = {
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    completed: 'bg-blue-50 text-blue-700 border-blue-200',
  };

  const typeColors = {
    annual: 'bg-sky-100 text-sky-700',
    sick: 'bg-rose-100 text-rose-700',
    emergency: 'bg-orange-100 text-orange-700',
    unpaid: 'bg-slate-100 text-slate-600',
    maternity: 'bg-pink-100 text-pink-700',
    paternity: 'bg-violet-100 text-violet-700',
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  const initials = profile?.name?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-dashboard">

      {/* Live clock-in status */}
      <ClockStatusPill />

      {/* Work Anniversary Celebration */}
      <AnniversaryCelebration joinDate={profile?.join_date} employeeName={profile?.name} />

      {/* HR On Leave notice — visible to everyone */}
      {(hrOnLeave.current.length > 0 || hrOnLeave.upcoming.length > 0 || hrOnLeave.managers_current.length > 0 || hrOnLeave.managers_upcoming.length > 0) && (
        <Card className="border-amber-200 bg-amber-50 overflow-hidden" data-testid="hr-on-leave-banner">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Palmtree className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">Team Availability</span>
            </div>
            {hrOnLeave.current.map((h, i) => (
              <div key={`c${i}`} className="text-xs text-amber-800 flex items-start gap-2 py-1" data-testid="hr-on-leave-current">
                <Badge className="bg-amber-500 text-white shrink-0">On leave now</Badge>
                <span>
                  <strong>{h.employee_name}</strong> ({h.designation}) is on {h.leave_type || 'leave'} — back on{' '}
                  {new Date(new Date(h.end_date).getTime() + 86400000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}.
                </span>
              </div>
            ))}
            {hrOnLeave.upcoming.map((h, i) => (
              <div key={`u${i}`} className="text-xs text-amber-700 flex items-start gap-2 py-1" data-testid="hr-on-leave-upcoming">
                <Badge variant="outline" className="border-amber-400 text-amber-700 shrink-0">Upcoming</Badge>
                <span>
                  <strong>{h.employee_name}</strong> ({h.designation}) — {h.leave_type || 'leave'} from{' '}
                  {new Date(h.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} to{' '}
                  {new Date(h.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}.
                </span>
              </div>
            ))}

            {/* HR cover contact — who to reach while HR is away */}
            {hrOnLeave.current.length > 0 && hrOnLeave.cover_contacts.length > 0 && (
              <div className="mt-2 pt-2 border-t border-amber-200 text-xs text-amber-800" data-testid="hr-cover-contact">
                <span className="font-semibold">For urgent HR matters, contact: </span>
                {hrOnLeave.cover_contacts.map((c, i) => (
                  <span key={i}>
                    {i > 0 && ' · '}
                    <strong>{c.name}</strong>{c.designation ? ` (${c.designation})` : ''}
                    {c.phone ? ` — ${c.phone}` : (c.email ? ` — ${c.email}` : '')}
                  </span>
                ))}
              </div>
            )}

            {/* Managers on leave */}
            {(hrOnLeave.managers_current.length > 0 || hrOnLeave.managers_upcoming.length > 0) && (
              <div className="mt-2 pt-2 border-t border-amber-200" data-testid="managers-on-leave">
                <p className="text-[11px] font-semibold text-amber-700 mb-1">Managers away</p>
                {hrOnLeave.managers_current.map((m, i) => (
                  <div key={`mc${i}`} className="text-xs text-amber-800 flex items-start gap-2 py-0.5">
                    <Badge className="bg-orange-500 text-white shrink-0">On leave now</Badge>
                    <span><strong>{m.employee_name}</strong> ({m.designation}) — back on {new Date(new Date(m.end_date).getTime() + 86400000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}.</span>
                  </div>
                ))}
                {hrOnLeave.managers_upcoming.map((m, i) => (
                  <div key={`mu${i}`} className="text-xs text-amber-700 flex items-start gap-2 py-0.5">
                    <Badge variant="outline" className="border-orange-400 text-orange-700 shrink-0">Upcoming</Badge>
                    <span><strong>{m.employee_name}</strong> ({m.designation}) — {new Date(m.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} to {new Date(m.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}.</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Profile Card */}
      <Card className="border-0 shadow-lg overflow-hidden" data-testid="profile-card">
        <div className="h-24 bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700 relative">
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
            <div className="w-20 h-20 rounded-full border-4 border-white shadow-lg overflow-hidden bg-white flex items-center justify-center">
              {profile?.photo_url ? (
                <img src={`${API}${profile.photo_url}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-sky-400">{initials}</span>
              )}
            </div>
          </div>
        </div>
        <CardContent className="pt-12 pb-5 px-5 text-center bg-gradient-to-b from-blue-50 via-sky-50 to-white">
          <h2 className="text-lg font-bold text-gray-900">{profile?.name}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{profile?.position}</p>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Badge className={`text-[10px] px-2 py-0.5 ${
              profile?.employment_status === 'Active'
                ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                : 'bg-red-100 text-red-700 border-red-200'
            }`}>
              {profile?.employment_status || 'Active'}
            </Badge>
            <Badge variant="outline" className="text-[10px] px-2 py-0.5">{profile?.employee_role}</Badge>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-4 text-xs text-gray-500">
            {profile?.email && (
              <span className="flex items-center gap-1"><Mail className="h-3 w-3 text-sky-400" />{profile.email}</span>
            )}
            {profile?.phone && (
              <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-sky-400" />{profile.phone}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* HR News Desk — Continuous Upward Scroll */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="news-desk-card">
        <CardContent className="p-0">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Newspaper className="h-4 w-4 text-white" />
              <h3 className="text-xs font-bold text-white uppercase tracking-wider">HR News Desk</h3>
            </div>
            <button
              onClick={() => navigate('/employee-portal/news-desk')}
              className="text-[10px] font-semibold text-white/80 hover:text-white transition-colors"
              data-testid="news-view-all-btn"
            >
              View All &rarr;
            </button>
          </div>
          {(newsJoiners.length > 0 || newsCustom.length > 0) ? (
            <div className="news-desk-viewport" ref={scrollRef}>
              <div className="news-desk-track">
                {/* Duplicate items for seamless loop */}
                {[...[...newsJoiners.map(j => ({...j, _type: 'joiner'})), ...newsCustom.map(c => ({...c, _type: 'custom'}))],
                  ...[...newsJoiners.map(j => ({...j, _type: 'joiner'})), ...newsCustom.map(c => ({...c, _type: 'custom'}))]
                ].map((item, idx) => {
                  if (item._type === 'joiner') {
                    const joinDate = new Date(item.join_date + 'T00:00:00');
                    const formatted = joinDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    return (
                      <div key={`joiner-${item.id}-${idx}`} className="px-4 py-3 border-b border-gray-100" data-testid={`news-joiner-${item.id}-${idx}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            {item.photo_url ? (
                              <img src={`${API}${item.photo_url}`} alt="" className="w-9 h-9 rounded-full object-cover" />
                            ) : (
                              <User className="h-4 w-4 text-indigo-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-800 truncate">
                              Welcome <span className="text-indigo-600">{item.name}</span>
                            </p>
                            <p className="text-[10px] text-gray-500">{item.position} &bull; {item.department}</p>
                          </div>
                          <Badge variant="outline" className="text-[9px] flex-shrink-0 border-indigo-200 text-indigo-600">{formatted}</Badge>
                        </div>
                        <p className="text-[10px] text-gray-400 italic mt-1.5 ml-12 leading-relaxed">Welcome to the team! We're excited to have you on board and look forward to your contributions. Wishing you great success and a rewarding journey with us!</p>
                      </div>
                    );
                  } else {
                    return (
                      <div key={`custom-${item.id}-${idx}`} className="px-4 py-3 border-b border-gray-100 flex items-center gap-3" data-testid={`news-custom-${item.id}-${idx}`}>
                        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <Newspaper className="h-4 w-4 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-800 truncate">{item.title}</p>
                          <p className="text-[10px] text-gray-500">{item.message}</p>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          ) : (
            <div className="py-6 text-center">
              <Newspaper className="h-8 w-8 mx-auto mb-2 text-gray-200" />
              <p className="text-xs text-gray-400">No news or announcements</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assigned Tasks Section */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-violet-50 to-purple-50" data-testid="assigned-tasks-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
              <ListChecks className="h-3.5 w-3.5 text-violet-500" />
              My Tasks
            </h3>
            <button onClick={() => navigate('/employee-portal/todos')} className="text-[10px] text-sky-600 font-medium">
              View All <ChevronRight className="h-3 w-3 inline" />
            </button>
          </div>
          {(() => {
            const activeTasks = assignedTasks.filter(t => t.status === 'active');
            if (activeTasks.length === 0) {
              return (
                <div className="text-center py-4" data-testid="no-active-tasks">
                  <p className="text-sm text-gray-400">0 Active tasks</p>
                </div>
              );
            }
            return (
              <div className="space-y-2">
                {activeTasks.slice(0, 4).map(task => {
                  const isOverdue = task.due_date && new Date(task.due_date + 'T23:59:59') < new Date();
                  const priorityCfg = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-green-100 text-green-700' };
                  return (
                    <div key={task.id} className="flex items-start gap-2.5 p-2.5 bg-gray-50 rounded-lg" data-testid={`dashboard-task-${task.id}`}>
                      <div className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border-2 border-violet-300" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate">{task.title}</p>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <Badge className={`text-[8px] px-1 py-0 ${priorityCfg[task.priority] || priorityCfg.medium}`}>{task.priority}</Badge>
                          <span className="text-[9px] text-violet-500 flex items-center gap-0.5">
                            <UserCheck className="h-2.5 w-2.5" /> {task.assigned_by_type === 'self' ? 'Personal' : task.assigned_by_name}
                          </span>
                          {task.due_date && (
                            <span className={`text-[9px] flex items-center gap-0.5 ${isOverdue ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                              <Calendar className="h-2.5 w-2.5" />
                              {new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                              {isOverdue && ' (overdue)'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {activeTasks.length > 4 && (
                  <p className="text-[10px] text-center text-gray-400">+{activeTasks.length - 4} more tasks</p>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Document Expiry Alerts */}
      {docExpiry.some(d => d.status !== 'ok' && d.status !== 'not_set') && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-orange-50" data-testid="doc-expiry-card">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              Document Expiry Alerts
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {docExpiry.filter(d => d.status !== 'ok' && d.status !== 'not_set').map(doc => (
                <div key={doc.document_type} className={`p-3 rounded-xl text-center ${
                  doc.status === 'expired' ? 'bg-red-100/80 border border-red-200' :
                  doc.status === 'critical' ? 'bg-red-100/80 border border-red-200' :
                  doc.status === 'urgent' ? 'bg-orange-100/80 border border-orange-200' :
                  'bg-white/80 border border-amber-200'
                }`}>
                  <FileText className={`h-5 w-5 mx-auto mb-1.5 ${
                    doc.status === 'expired' || doc.status === 'critical' ? 'text-red-500' :
                    doc.status === 'urgent' ? 'text-orange-500' : 'text-amber-500'
                  }`} />
                  <p className="text-xs font-bold text-gray-800">{doc.document_type}</p>
                  <p className="text-[9px] text-gray-400 truncate">{doc.document_number || 'N/A'}</p>
                  <div className="mt-2">
                    <Badge className={`text-[10px] ${
                      doc.status === 'expired' ? 'bg-red-500 text-white' :
                      doc.status === 'critical' ? 'bg-red-500 text-white' :
                      doc.status === 'urgent' ? 'bg-orange-500 text-white' :
                      'bg-amber-500 text-white'
                    }`}>
                      {doc.status === 'expired' ? 'EXPIRED' : `${doc.days_until_expiry}d left`}
                    </Badge>
                  </div>
                  {doc.expiry_date && (
                    <p className="text-[9px] text-gray-400 mt-1">
                      {new Date(doc.expiry_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All-Staff Document Expiry Alerts (Super Admin — EMP0001) */}
      {allStaffExpiry.length > 0 && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-rose-50 to-red-50" data-testid="all-staff-doc-expiry-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wider flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-red-500" />
                All Staff Document Alerts
              </h3>
              <Badge className="text-[10px] bg-red-500 text-white">{allStaffExpiry.length}</Badge>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {allStaffExpiry.map((doc, idx) => (
                <div
                  key={`${doc.employee_id}-${doc.document_type}-${idx}`}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border ${
                    doc.status === 'expired' || doc.status === 'critical' ? 'bg-red-50 border-red-200' :
                    doc.status === 'urgent' ? 'bg-orange-50 border-orange-200' :
                    'bg-white border-amber-200'
                  }`}
                  data-testid={`all-staff-doc-${doc.employee_code}-${doc.document_type}`}
                >
                  <div className="w-8 h-8 rounded-full border-2 border-white shadow-sm flex-shrink-0 overflow-hidden bg-red-100 flex items-center justify-center">
                    {doc.photo_url ? (
                      <img src={doc.photo_url.startsWith('/') ? `${API}${doc.photo_url}` : doc.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{doc.employee_name}</p>
                    <p className="text-[10px] text-gray-500 truncate">
                      {doc.document_type} &bull; {doc.employee_code}{doc.department ? ` \u00b7 ${doc.department}` : ''}
                    </p>
                    {doc.expiry_date && (
                      <p className="text-[9px] text-gray-400">
                        Exp: {new Date(doc.expiry_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <Badge className={`text-[10px] flex-shrink-0 ${
                    doc.status === 'expired' ? 'bg-red-600 text-white' :
                    doc.status === 'critical' ? 'bg-red-500 text-white' :
                    doc.status === 'urgent' ? 'bg-orange-500 text-white' :
                    'bg-amber-500 text-white'
                  }`}>
                    {doc.status === 'expired' ? 'EXPIRED' : `${doc.days_until_expiry}d`}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {teamStatus.onboarded?.length > 0 && (
        <Card className="border-0 shadow-sm overflow-hidden" data-testid="onboarded-card">
          <CardContent className="p-0">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
              <div className="flex items-center gap-1.5 mb-3">
                <UserCheck className="h-3.5 w-3.5 text-emerald-600" />
                <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">New Joiners</p>
                <span className="ml-auto text-[9px] text-emerald-500 font-medium">{teamStatus.onboarded.length} in 90 days</span>
              </div>
              <div className="space-y-2">
                {teamStatus.onboarded.slice(0, 5).map((m) => {
                  const jd = new Date(m.join_date + 'T00:00:00');
                  return (
                    <div key={m.employee_code} className="flex items-center gap-2.5" data-testid={`joiner-${m.employee_code}`}>
                      <div className="w-8 h-8 rounded-full border-2 border-white shadow-sm flex-shrink-0 overflow-hidden bg-emerald-200">
                        {m.photo_url ? (
                          <img src={m.photo_url.startsWith('/') ? `${API}${m.photo_url}` : m.photo_url} alt={m.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-[10px] font-bold text-emerald-700">{m.name?.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-gray-800 truncate">{m.name}</p>
                        <p className="text-[9px] text-gray-400 truncate">{m.department} &middot; {m.position}</p>
                      </div>
                      <div className="flex-shrink-0 bg-white rounded-lg px-2 py-1 text-center shadow-sm border border-emerald-100">
                        <p className="text-[8px] text-emerald-400 uppercase leading-tight">{jd.toLocaleDateString('en-GB', { month: 'short' })}</p>
                        <p className="text-sm font-bold text-emerald-600 leading-tight">{jd.getDate()}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {teamStatus.onboarded.length > 5 && (
                <p className="text-[8px] text-emerald-400 mt-2 text-center">+{teamStatus.onboarded.length - 5} more</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Highlights: On Vacation (Department) ──────── */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="on-vacation-card">
        <CardContent className="p-0">
          <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-4">
            <div className="flex items-center gap-1.5 mb-3">
              <Palmtree className="h-3.5 w-3.5 text-sky-600" />
              <p className="text-[10px] font-bold text-sky-700 uppercase tracking-wider">On Vacation</p>
              <span className="ml-auto text-[9px] text-sky-500 font-medium">{teamStatus.on_leave?.length || 0} today</span>
            </div>
            {teamStatus.on_leave?.length > 0 ? (
              <div className="space-y-2">
                {teamStatus.on_leave.slice(0, 5).map((m) => {
                  // Back = the day after the last leave day
                  const endDate = m.end_date ? new Date(m.end_date + 'T00:00:00') : null;
                  if (endDate) endDate.setDate(endDate.getDate() + 1);
                  return (
                    <div key={m.employee_code} className="flex items-center gap-2.5" data-testid={`vacation-${m.employee_code}`}>
                      <div className="w-8 h-8 rounded-full border-2 border-white shadow-sm flex-shrink-0 overflow-hidden bg-sky-200">
                        {m.photo_url ? (
                          <img src={m.photo_url.startsWith('/') ? `${API}${m.photo_url}` : m.photo_url} alt={m.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-[10px] font-bold text-sky-700">{m.name?.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-gray-800 truncate">{m.name}</p>
                        <p className="text-[9px] text-gray-400 truncate">{m.department} &middot; {m.position}</p>
                      </div>
                      {endDate && (
                        <div className="flex-shrink-0 bg-white rounded-lg px-2 py-1 text-center shadow-sm border border-sky-100">
                          <p className="text-[8px] text-sky-400 uppercase leading-tight">Back</p>
                          <p className="text-[10px] font-bold text-sky-600 leading-tight">{endDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
                {teamStatus.on_leave.length > 5 && (
                  <p className="text-[8px] text-sky-400 mt-2 text-center">+{teamStatus.on_leave.length - 5} more</p>
                )}
              </div>
            ) : (
              <div className="text-center py-3">
                <Palmtree className="h-7 w-7 mx-auto mb-1.5 text-sky-200" />
                <p className="text-[10px] text-sky-400">No one on vacation today</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Team Status: On Leave / Sick / Birthdays */}
      {(teamStatus.on_sick.length > 0 || teamStatus.birthdays.length > 0) && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-sky-50" data-testid="team-status-card">
          <CardContent className="p-4 space-y-4">
            <h3 className="text-xs font-semibold text-indigo-500 uppercase tracking-wider flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              Team Status
            </h3>

            {/* Sick Leave Today */}
            {teamStatus.on_sick.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Thermometer className="h-3 w-3" /> Sick Leave Today
                </p>
                {(() => {
                  const byDept = {};
                  teamStatus.on_sick.forEach(e => {
                    const d = e.department || 'Other';
                    if (!byDept[d]) byDept[d] = [];
                    byDept[d].push(e);
                  });
                  return Object.entries(byDept).map(([dept, members]) => (
                    <div key={dept} className="mb-2">
                      <p className="text-[9px] font-semibold text-gray-400 mb-1">{dept}</p>
                      <div className="space-y-1.5">
                        {members.map(m => (
                          <div key={m.employee_code} className="flex items-center gap-2.5 px-3 py-2 bg-red-50 rounded-lg border border-red-100" data-testid={`sick-${m.employee_code}`}>
                            <div className="w-7 h-7 rounded-full bg-red-200 flex items-center justify-center flex-shrink-0">
                              {m.photo_url
                                ? <img src={m.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                                : <User className="h-3.5 w-3.5 text-red-600" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">{m.name}</p>
                              <p className="text-[9px] text-gray-400">{m.position}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <Badge className="text-[8px] px-1.5 py-0 bg-red-100 text-red-700">Sick</Badge>
                              <p className="text-[8px] text-gray-400 mt-0.5">until {new Date(m.end_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}

            {/* Birthdays */}
            {teamStatus.birthdays.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-pink-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Cake className="h-3 w-3" /> Upcoming Birthdays
                </p>
                {(() => {
                  const byDept = {};
                  teamStatus.birthdays.forEach(e => {
                    const d = e.department || 'Other';
                    if (!byDept[d]) byDept[d] = [];
                    byDept[d].push(e);
                  });
                  return Object.entries(byDept).map(([dept, members]) => (
                    <div key={dept} className="mb-2">
                      <p className="text-[9px] font-semibold text-gray-400 mb-1">{dept}</p>
                      <div className="space-y-1.5">
                        {members.map(m => (
                          <div key={m.employee_code} className="flex items-center gap-2.5 px-3 py-2 bg-pink-50 rounded-lg border border-pink-100" data-testid={`birthday-${m.employee_code}`}>
                            <div className="w-7 h-7 rounded-full bg-pink-200 flex items-center justify-center flex-shrink-0">
                              {m.photo_url
                                ? <img src={m.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                                : <Cake className="h-3.5 w-3.5 text-pink-600" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">{m.name}</p>
                              <p className="text-[9px] text-gray-400">{m.position}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              {m.is_today ? (
                                <Badge className="text-[8px] px-1.5 py-0 bg-pink-500 text-white animate-pulse">Today!</Badge>
                              ) : (
                                <p className="text-[9px] text-pink-600 font-medium">
                                  {new Date(m.birthday + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upcoming Leaves — managers see their departments, HR sees all */}
      {upcomingLeaves.visible && upcomingLeaves.leaves.length > 0 && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="upcoming-leaves-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-emerald-600 uppercase tracking-wider flex items-center gap-1.5">
                <Palmtree className="h-3.5 w-3.5" /> Upcoming Leaves
              </h3>
              <Badge className="text-[8px] px-1.5 py-0 bg-emerald-100 text-emerald-700">
                {upcomingLeaves.see_all ? 'All Departments' : 'My Departments'}
              </Badge>
            </div>
            {(() => {
              const byDept = {};
              upcomingLeaves.leaves.forEach(e => {
                const d = e.department || 'Other';
                if (!byDept[d]) byDept[d] = [];
                byDept[d].push(e);
              });
              return Object.entries(byDept).map(([dept, members]) => (
                <div key={dept} className="mb-1">
                  <p className="text-[9px] font-semibold text-gray-400 mb-1">{dept}</p>
                  <div className="space-y-1.5">
                    {members.map((m, i) => (
                      <div key={`${m.employee_code}-${m.start_date}-${i}`} className="flex items-center gap-2.5 px-3 py-2 bg-white/70 rounded-lg border border-emerald-100" data-testid={`upcoming-leave-${m.employee_code}-${i}`}>
                        <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          {m.photo_url
                            ? <img src={m.photo_url.startsWith('/') ? `${API}${m.photo_url}` : m.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                            : <User className="h-3.5 w-3.5 text-emerald-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{m.name}</p>
                          <p className="text-[9px] text-gray-400 capitalize">{(m.leave_type || '').replace('_', ' ')} · {m.days} day{m.days === 1 ? '' : 's'}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[9px] font-semibold text-emerald-700">
                            {new Date(m.start_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </p>
                          <p className="text-[8px] text-gray-400">
                            to {new Date(m.end_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </CardContent>
        </Card>
      )}

      {/* Basic Information */}
      <Card className="border-0 shadow-sm" data-testid="basic-info-card">
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Basic Information</h3>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-sky-50 rounded-xl p-3 text-center">
              <CalendarDays className="h-5 w-5 text-sky-500 mx-auto mb-1.5" />
              <p className="text-[10px] text-gray-400">Hired Date</p>
              <p className="text-xs font-semibold text-gray-800 mt-0.5">
                {profile?.join_date ? new Date(profile.join_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014'}
              </p>
            </div>
            <div className="bg-sky-50 rounded-xl p-3 text-center">
              <Clock className="h-5 w-5 text-sky-500 mx-auto mb-1.5" />
              <p className="text-[10px] text-gray-400">Tenure</p>
              <p className="text-xs font-semibold text-gray-800 mt-0.5">{calcTenure(profile?.join_date)}</p>
            </div>
            <div className="bg-sky-50 rounded-xl p-3 text-center">
              <Shield className="h-5 w-5 text-sky-500 mx-auto mb-1.5" />
              <p className="text-[10px] text-gray-400">Emp ID</p>
              <p className="text-xs font-semibold text-gray-800 mt-0.5">{profile?.employee_code}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Occupation Info */}
      <Card className="border-0 shadow-sm" data-testid="occupation-card">
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Occupation</h3>
          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center gap-1.5 p-2">
              <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-teal-500" />
              </div>
              <p className="text-[10px] text-gray-500 text-center">{profile?.employee_role || 'Staff'}</p>
            </div>
            <div className="flex flex-col items-center gap-1.5 p-2">
              <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-teal-500" />
              </div>
              <p className="text-[10px] text-gray-500 text-center">{profile?.department}</p>
            </div>
            <div className="flex flex-col items-center gap-1.5 p-2">
              <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center">
                <MapPin className="h-5 w-5 text-teal-500" />
              </div>
              <p className="text-[10px] text-gray-500 text-center">{profile?.nationality || 'N/A'}</p>
            </div>
          </div>
          {profile?.reporting_managers?.length > 0 && (
            <div className="mt-3 pt-3 border-t">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Reporting To</p>
              {profile.reporting_managers.map((mgr, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Users className="h-3.5 w-3.5 text-indigo-500" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-800">{mgr.name}</p>
                    <p className="text-[10px] text-gray-400">{mgr.position}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leave Balance */}
      {balance && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-sky-50 to-cyan-50" data-testid="balance-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-sky-500 uppercase tracking-wider">Leave Balance</h3>
              <Badge
                variant="outline"
                className="text-[10px] cursor-help"
                title={balance.cycle_label ? `Cycle: ${balance.cycle_label}${balance.rule_name && balance.rule_name !== 'Default' ? ` (${balance.rule_name})` : ''}` : `Year ${balance.year}`}
                data-testid="balance-cycle-badge"
              >
                {balance.year}
              </Badge>
            </div>
            <div className="bg-gradient-to-r from-sky-50 to-blue-50 rounded-xl p-4 mb-3">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-[10px] text-sky-600 uppercase tracking-wider">Annual Balance</p>
                  <p className="text-3xl font-bold text-sky-700 mt-1">{balance.annual_balance?.toFixed(1)}<span className="text-base font-normal text-sky-400 ml-1">days</span></p>
                  {balance.cycle_label && (
                    <p className="text-[10px] text-sky-500/70 mt-1" data-testid="balance-cycle-window">
                      Cycle: {balance.cycle_label}
                    </p>
                  )}
                </div>
                <div className="text-right space-y-0.5">
                  <p className="text-[10px] text-gray-400">Entitlement: <span className="font-medium text-gray-600">{balance.annual_entitlement?.toFixed(1)}</span></p>
                  <p className="text-[10px] text-gray-400">Carry Fwd: <span className="font-medium text-gray-600">{balance.carry_forward?.toFixed(1)}</span></p>
                  <p className="text-[10px] text-gray-400">Taken: <span className="font-medium text-gray-600">{balance.annual_taken?.toFixed(1)}</span></p>
                </div>
              </div>
              <div className="w-full bg-sky-200/50 rounded-full h-1.5 mt-3">
                <div
                  className="bg-sky-500 rounded-full h-1.5 transition-all"
                  style={{ width: `${Math.min(100, Math.max(0, ((balance.annual_total - balance.annual_balance) / Math.max(balance.annual_total, 1)) * 100))}%` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              <div className="text-center py-2 px-1 rounded-lg bg-rose-50">
                <p className="text-lg font-bold text-rose-600">{balance.sick_taken?.toFixed(0) || 0}</p>
                <p className="text-[9px] text-rose-400">Sick</p>
              </div>
              <div className="text-center py-2 px-1 rounded-lg bg-orange-50">
                <p className="text-lg font-bold text-orange-600">{balance.emergency_taken?.toFixed(0) || 0}</p>
                <p className="text-[9px] text-orange-400">Emergency</p>
              </div>
              <div className="text-center py-2 px-1 rounded-lg bg-slate-50">
                <p className="text-lg font-bold text-slate-600">{balance.unpaid_taken?.toFixed(0) || 0}</p>
                <p className="text-[9px] text-slate-400">Unpaid</p>
              </div>
              <div className="text-center py-2 px-1 rounded-lg bg-teal-50" data-testid="balance-ph-available">
                <p className="text-lg font-bold text-teal-600">{balance.ph_available?.toFixed(0) || 0}</p>
                <p className="text-[9px] text-teal-500">PH Bal</p>
              </div>
              <div className="text-center py-2 px-1 rounded-lg bg-sky-50">
                <p className="text-lg font-bold text-sky-600">{balance.total_taken?.toFixed(0) || 0}</p>
                <p className="text-[9px] text-sky-400">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick action: Apply for Leave (Clock In/Out now lives in the pill above) */}
      <div>
        <Button
          className="w-full py-5 text-sm font-semibold shadow-md bg-sky-600 hover:bg-sky-700"
          onClick={() => setShowApply(true)}
          data-testid="apply-leave-btn"
        >
          <PlusCircle className="h-5 w-5 mr-2" /> Apply for Leave
        </Button>
      </div>

      {/* Recent Leaves */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-rose-50 to-pink-50" data-testid="recent-leaves-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-rose-500 uppercase tracking-wider">Recent Leaves</h3>
            <button
              onClick={() => navigate('/employee-portal/leaves')}
              className="text-[10px] text-sky-600 font-medium flex items-center gap-0.5"
            >
              View All <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          {recentLeaves.length === 0 ? (
            <div className="text-center py-6">
              <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-200" />
              <p className="text-xs text-gray-400">No leave records yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentLeaves.map(leave => (
                <div key={leave.id} className={`p-3 rounded-lg border ${statusColors[leave.status] || 'border-gray-100 bg-gray-50'}`} data-testid={`recent-leave-${leave.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold flex-shrink-0 ${typeColors[leave.leave_type] || 'bg-gray-100'}`}>
                        {leave.leave_type?.toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-700">
                          {leave.group_rows
                            ? <span data-testid={`recent-leave-dates-${leave.id}`}>{leave.group_rows.map((r) => <span key={r.id} className={r.status === 'rejected' ? 'line-through text-gray-400 mr-1' : 'mr-1'}>{shortDate(r.start_date)}</span>)}</span>
                            : <>{leave.start_date} &rarr; {leave.end_date}</>}
                        </p>
                        <p className="text-[10px] text-gray-400 truncate">{leave.days} day{leave.days !== 1 ? 's' : ''}{leave.reason ? ` \u2014 ${leave.reason}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                      {leave.status === 'pending' && (
                        <>
                          {!leave.group_rows && <button
                            onClick={() => setEditingLeave(leave)}
                            className="p-1.5 rounded-md hover:bg-sky-100 text-sky-500 transition-colors"
                            title="Edit"
                            data-testid={`edit-leave-${leave.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>}
                          <button
                            onClick={() => setDeletingLeaveId(leave.id)}
                            className="p-1.5 rounded-md hover:bg-red-100 text-red-400 transition-colors"
                            title={leave.group_rows ? 'Delete the whole request' : 'Delete'}
                            data-testid={`delete-leave-${leave.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      <span className="text-[10px] font-semibold capitalize">{leave.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Apply Leave Dialog */}
      {showApply && (
        <ApplyLeaveDialog
          token={token}
          phAvailable={balance?.ph_available || 0}
          onClose={() => setShowApply(false)}
          onSuccess={() => { setShowApply(false); loadData(); }}
        />
      )}

      {/* Edit Leave Dialog */}
      {editingLeave && (
        <EditLeaveDialog
          token={token}
          leave={editingLeave}
          onClose={() => setEditingLeave(null)}
          onSuccess={() => { setEditingLeave(null); loadData(); }}
        />
      )}

      {/* Delete Confirmation */}
      {deletingLeaveId && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid="delete-leave-confirm">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDeletingLeaveId(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 mx-4 max-w-sm w-full z-10 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <Trash2 className="h-5 w-5 text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Delete Leave?</h3>
            <p className="text-sm text-gray-500 mb-4">This will permanently remove this pending leave application.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeletingLeaveId(null)}>Cancel</Button>
              <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white" onClick={() => handleDeleteLeave(deletingLeaveId)} data-testid="confirm-delete-leave">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Apply Leave Dialog ─────────────────────────────────────── */
const ApplyLeaveDialog = ({ token, phAvailable = 0, onClose, onSuccess }) => {
  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [clash, setClash] = useState(null);
  const [mode, setMode] = useState('range');
  const [multiDates, setMultiDates] = useState([]);
  const [pickerKey, setPickerKey] = useState(0);
  const [mdCfg, setMdCfg] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/employee-portal/multi-date-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMdCfg(r.data)).catch(() => setMdCfg(null));
  }, [token]);
  const multiAllowed = !!mdCfg?.enabled && !(mdCfg.excluded_types || []).includes((leaveType || '').toLowerCase());
  const isMulti = mode === 'multi' && multiAllowed;

  useEffect(() => {
    if (!startDate || !endDate || new Date(startDate) > new Date(endDate)) { setClash(null); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const chk = await axios.get(`${API}/api/employee-portal/leave-overlap-check`, {
          params: { start_date: startDate, end_date: endDate },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setClash(chk.data);
      } catch { if (active) setClash(null); }
    }, 350);
    return () => { active = false; clearTimeout(t); };
  }, [startDate, endDate, token]);

  const calculateDays = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) return 0;
    const diffTime = end.getTime() - start.getTime();
    return Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const days = isMulti ? multiDates.length : calculateDays();
  const paternityWarning = leaveType === 'paternity' && days > 5;
  const isPH = leaveType === 'public_holiday';
  const phExceeded = isPH && days > phAvailable;

  const submitMulti = async () => {
    if (!leaveType || multiDates.length < 2) { setError('Pick a leave type and at least 2 dates'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/employee-portal/apply-leave-multi`, { leave_type: leaveType, dates: multiDates, reason },
        { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(true);
      setTimeout(() => onSuccess(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isMulti) { await submitMulti(); return; }
    if (!leaveType || !startDate || !endDate || days <= 0) {
      setError('Please fill all required fields with valid dates');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      // Pre-submit clash check — warn if anyone else in the same department is on leave
      try {
        const chk = await axios.get(`${API}/api/employee-portal/leave-overlap-check`, {
          params: { start_date: startDate, end_date: endDate },
          headers: { Authorization: `Bearer ${token}` },
        });
        const overlaps = chk.data?.overlaps || [];
        if (overlaps.length) {
          const lines = overlaps.slice(0, 5).map(o =>
            `• ${o.employee_name} (${o.leave_type}, ${o.start_date} → ${o.end_date})`
          ).join('\n');
          const more = overlaps.length > 5 ? `\n…and ${overlaps.length - 5} more` : '';
          const proceed = window.confirm(
            `⚠ Leave overlap in ${chk.data?.department || 'your department'}\n\n${overlaps.length} teammate(s) already have leave in this window:\n${lines}${more}\n\nDo you still want to submit your request?`
          );
          if (!proceed) { setSubmitting(false); return; }
        }
      } catch { /* overlap check is advisory; ignore failures */ }

      await axios.post(`${API}/api/employee-portal/apply-leave`, {
        leave_type: leaveType, start_date: startDate, end_date: endDate, days, reason
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(true);
      setTimeout(() => onSuccess(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="apply-leave-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-gray-900">Apply for Leave</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Send className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="font-bold text-gray-900">Leave Applied!</p>
            <p className="text-sm text-gray-500 mt-1">Your request has been submitted for approval.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <LeavePatterns token={token} leaveType={leaveType} reason={reason} dates={isMulti ? multiDates : []}
              onUse={(t) => {
                const blocked = !mdCfg?.enabled || (mdCfg.excluded_types || []).includes(t.leave_type);
                const filled = datesForNextMonth(t.weekdays, mdCfg?.max_days_ahead || 90).slice(0, mdCfg?.max_dates || 60);
                setLeaveType(t.leave_type);
                if (t.reason) setReason(t.reason);
                if (blocked) { setError(`"${t.name}" can't be used — this leave type isn't allowed for separate dates right now.`); return; }
                setMode('multi');
                setPickerKey((k) => k + 1);
                setMultiDates(filled);
                setError(filled.length < 2 ? `"${t.name}" gives only ${filled.length} date(s) next month — add at least one more date.` : '');
              }} />
            <div>
              <Label className="text-sm font-medium">Leave Type *</Label>
              <Select value={leaveType} onValueChange={setLeaveType}>
                <SelectTrigger className="mt-1" data-testid="dialog-leave-type">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {LEAVE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {multiAllowed && (
              <div className="grid grid-cols-2 gap-1 bg-gray-100 rounded-lg p-1" data-testid="leave-mode-toggle">
                {[['range', 'Date range'], ['multi', 'Separate dates']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setMode(k)} data-testid={`leave-mode-${k}`}
                    className={`py-1.5 rounded-md text-sm transition-colors ${mode === k ? 'bg-white shadow-sm font-semibold text-sky-700' : 'text-gray-500 hover:text-gray-800'}`}>{l}</button>
                ))}
              </div>
            )}
            {isMulti ? (
              <div>
                <Label className="text-sm font-medium">Dates *</Label>
                <p className="text-[11px] text-gray-500 mb-2">Add one or more date ranges (a single day = same From and To), or repeat a weekday. Your manager approves them all in one request.</p>
                <MultiDatePicker key={pickerKey} dates={multiDates} onChange={setMultiDates} maxDaysAhead={mdCfg?.max_days_ahead || 90} max={mdCfg?.max_dates || 60} testPrefix="portal-md" />
              </div>
            ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Start Date *</Label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1" data-testid="dialog-start-date" />
              </div>
              <div>
                <Label className="text-sm font-medium">End Date *</Label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate} className="mt-1" data-testid="dialog-end-date" />
              </div>
            </div>
            )}
            {days > 0 && !isMulti && (
              <div className="flex items-center gap-2 py-2 px-3 bg-sky-50 rounded-lg">
                <CalendarDays className="h-4 w-4 text-sky-600" />
                <p className="text-sm text-sky-700 font-medium">{days} day{days !== 1 ? 's' : ''}</p>
              </div>
            )}
            {!isMulti && clash && clash.count > 0 && (
              <div className={`py-2 px-3 rounded-lg border ${clash.warn ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-testid="clash-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${clash.warn ? 'text-red-500' : 'text-amber-500'}`} />
                  <div className="min-w-0">
                    <p className={`text-xs font-semibold ${clash.warn ? 'text-red-700' : 'text-amber-700'}`}>
                      {clash.warn
                        ? `Too many off in ${clash.department} — up to ${clash.peak_concurrent} people away at once`
                        : `${clash.count} teammate(s) in ${clash.department} already off on these dates`}
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {clash.overlaps.slice(0, 4).map((o, i) => (
                        <li key={i} className="text-[10px] text-gray-500">
                          {o.employee_name} · <span className="capitalize">{(o.leave_type || '').replace('_', ' ')}</span> · {o.start_date} → {o.end_date}
                        </li>
                      ))}
                      {clash.overlaps.length > 4 && <li className="text-[10px] text-gray-400">…and {clash.overlaps.length - 4} more</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {isPH && (
              <div className="flex items-center justify-between gap-2 py-2 px-3 bg-teal-50 border border-teal-200 rounded-lg" data-testid="ph-balance-info">
                <span className="text-xs text-teal-700">Public Holiday balance available</span>
                <span className="text-sm font-bold text-teal-700">{Number(phAvailable).toFixed(1)} day{phAvailable === 1 ? '' : 's'}</span>
              </div>
            )}
            {phExceeded && (
              <div className="flex items-start gap-2 py-2 px-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="ph-exceeded-warning">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">You have <strong>{Number(phAvailable).toFixed(1)}</strong> PH day{phAvailable === 1 ? '' : 's'} available but selected <strong>{days}</strong>. The extra day(s) may not be approved.</p>
              </div>
            )}
            {paternityWarning && (
              <div className="flex items-start gap-2 py-2 px-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="paternity-warning">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">Paternity leave is limited to a maximum of <strong>5 days</strong>. You have selected {days} days. Please adjust your dates.</p>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium">Reason</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Briefly describe your reason..." rows={3} className="mt-1" data-testid="dialog-reason" />
            </div>
            {error && <p className="text-sm text-red-500 text-center">{error}</p>}
            <Button type="submit" className="w-full py-5 bg-sky-600 hover:bg-sky-700" disabled={submitting || !leaveType || (isMulti ? multiDates.length < 2 : (!startDate || !endDate || days <= 0)) || paternityWarning} data-testid="dialog-submit-leave">
              {submitting ? 'Submitting...' : <><Send className="h-4 w-4 mr-2" /> {isMulti ? `Submit ${multiDates.length} Dates for Approval` : 'Submit Leave Application'}</>}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ── Edit Leave Dialog ─────────────────────────────────────── */
const EditLeaveDialog = ({ token, leave, onClose, onSuccess }) => {
  const [leaveType, setLeaveType] = useState(leave.leave_type || '');
  const [startDate, setStartDate] = useState(leave.start_date || '');
  const [endDate, setEndDate] = useState(leave.end_date || '');
  const [reason, setReason] = useState(leave.reason || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const calculateDays = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) return 0;
    const diffTime = end.getTime() - start.getTime();
    return Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const days = calculateDays();
  const paternityWarning = leaveType === 'paternity' && days > 5;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!leaveType || !startDate || !endDate || days <= 0) {
      setError('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.patch(`${API}/api/employee-portal/my-leaves/${leave.id}`, {
        leave_type: leaveType, start_date: startDate, end_date: endDate, days, reason
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="edit-leave-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-gray-900">Edit Leave</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <Label className="text-sm font-medium">Leave Type *</Label>
            <Select value={leaveType} onValueChange={setLeaveType}>
              <SelectTrigger className="mt-1" data-testid="edit-leave-type">
                <SelectValue placeholder="Select leave type" />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                {LEAVE_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Start Date *</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1" data-testid="edit-start-date" />
            </div>
            <div>
              <Label className="text-sm font-medium">End Date *</Label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate} className="mt-1" data-testid="edit-end-date" />
            </div>
          </div>
          {days > 0 && (
            <div className="flex items-center gap-2 py-2 px-3 bg-sky-50 rounded-lg">
              <CalendarDays className="h-4 w-4 text-sky-600" />
              <p className="text-sm text-sky-700 font-medium">{days} day{days !== 1 ? 's' : ''}</p>
            </div>
          )}
          {paternityWarning && (
            <div className="flex items-start gap-2 py-2 px-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">Paternity leave is limited to <strong>5 days</strong>. You have selected {days} days.</p>
            </div>
          )}
          <div>
            <Label className="text-sm font-medium">Reason</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Briefly describe your reason..." rows={3} className="mt-1" data-testid="edit-reason" />
          </div>
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <Button type="submit" className="w-full py-5 bg-sky-600 hover:bg-sky-700" disabled={submitting || !leaveType || !startDate || !endDate || days <= 0 || paternityWarning} data-testid="edit-submit-leave">
            {submitting ? 'Saving...' : <><Pencil className="h-4 w-4 mr-2" /> Save Changes</>}
          </Button>
        </form>
      </div>
    </div>
  );
};


export default PortalDashboard;
